import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, BriefcaseBusiness, Gauge, ReceiptText, WalletCards } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api, toastError } from "../../lib/api";

type CurrencyTotals = Record<string, number>;
type Dashboard = {
  period: { from_date: string; to_date: string };
  kpis: {
    assigned_doctors: number; active_referring_doctors: number; converted_doctors: number;
    doctor_conversion_rate: number; patient_referrals: number; converted_patients: number;
    patient_conversion_rate: number; open_opportunities: number;
    pipeline_values_by_currency: CurrencyTotals; verified_visits: number;
    completed_interactions: number; overdue_followups: number;
  };
  mileage: {
    approved_km: number; average_km_per_verified_visit: number;
    approved_by_currency: CurrencyTotals; pending_by_currency: CurrencyTotals; rejected_count: number;
  };
  commissions: {
    eligible_conversions: number; non_commissionable_conversions: number;
    pending_by_currency: CurrencyTotals; approved_by_currency: CurrencyTotals; paid_by_currency: CurrencyTotals;
  };
  pipeline_by_stage: Record<string, { count: number; values_by_currency: CurrencyTotals }>;
  priority_pipeline: Array<{
    id: string; name: string; stage: string; value: number; currency: string;
    expected_close_date?: string; aging_days: number; detail_path: string;
  }>;
  pending_submissions: {
    reimbursements: { count: number; amounts_by_currency: CurrencyTotals };
    commissions: { count: number; amounts_by_currency: CurrencyTotals };
    invoices: { count: number; amounts_by_currency: CurrencyTotals };
    items: Array<{
      type: string; reference: string; submitted_at?: string; amount: number; currency: string;
      status: string; waiting_days: number; detail_path?: string;
    }>;
  };
  needs_attention: Array<{ type: string; count: number; label: string; detail_path?: string }>;
};

type Period = "this_month" | "last_month" | "quarter" | "year" | "custom";
const iso = (date: Date) => [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
const money = (totals: CurrencyTotals = {}) =>
  Object.entries(totals).length
    ? Object.entries(totals).map(([currency, amount]) =>
        new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount)).join(" · ")
    : "—";

function rangeFor(period: Period) {
  const today = new Date();
  let from = new Date(today.getFullYear(), today.getMonth(), 1);
  let to = today;
  if (period === "last_month") {
    from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    to = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (period === "quarter") {
    from = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
  } else if (period === "year") {
    from = new Date(today.getFullYear(), 0, 1);
  }
  return { from: iso(from), to: iso(to) };
}

const Card = ({ label, value, detail }: { label: string; value: React.ReactNode; detail?: string }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
    {detail && <div className="mt-1 text-xs text-slate-500">{detail}</div>}
  </div>
);

export default function MyPerformance() {
  const [period, setPeriod] = useState<Period>("this_month");
  const initial = useMemo(() => rangeFor("this_month"), []);
  const [fromDate, setFromDate] = useState(initial.from);
  const [toDate, setToDate] = useState(initial.to);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Dashboard>("/tenant/my-performance", {
        params: { from_date: fromDate, to_date: toDate },
      });
      setDashboard(data);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { void load(); }, [load]);

  const changePeriod = (value: Period) => {
    setPeriod(value);
    if (value !== "custom") {
      const range = rangeFor(value);
      setFromDate(range.from);
      setToDate(range.to);
    }
  };

  const k = dashboard?.kpis;
  const pending = dashboard?.pending_submissions;
  return (
    <>
      <PageHeader
        title="My Performance"
        subtitle="Your results, priorities, and submitted payments in one personal command center"
        actions={<Button variant="outline" onClick={load}>Refresh</Button>}
      />
      <main className="mx-auto max-w-7xl space-y-6 p-4 md:p-8" data-testid="my-performance-dashboard">
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-medium text-slate-600">Period
              <select className="mt-1 block h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={period}
                onChange={(event) => changePeriod(event.target.value as Period)} data-testid="performance-period">
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
                <option value="custom">Custom</option>
              </select>
            </label>
            {period === "custom" && <>
              <label className="text-xs font-medium text-slate-600">From<Input className="mt-1" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} /></label>
              <label className="text-xs font-medium text-slate-600">To<Input className="mt-1" type="date" value={toDate} onChange={e => setToDate(e.target.value)} /></label>
            </>}
            <span className="pb-2 text-xs text-slate-500">{fromDate} to {toDate}</span>
          </div>
        </section>

        {loading && !dashboard ? <div className="py-16 text-center text-sm text-slate-500">Loading your performance…</div> : <>
          <section>
            <div className="mb-3 flex items-center gap-2"><Gauge className="h-5 w-5" /><h2 className="font-semibold">Performance overview</h2></div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Card label="Assigned doctors" value={k?.assigned_doctors ?? 0} detail={`${k?.active_referring_doctors ?? 0} actively referring`} />
              <Card label="Doctors converted" value={k?.converted_doctors ?? 0} detail={`${k?.doctor_conversion_rate ?? 0}% of assigned doctors`} />
              <Card label="Patient referrals" value={k?.patient_referrals ?? 0} detail={`${k?.converted_patients ?? 0} converted · ${k?.patient_conversion_rate ?? 0}%`} />
              <Card label="Open pipeline" value={k?.open_opportunities ?? 0} detail={money(k?.pipeline_values_by_currency)} />
              <Card label="Verified visits" value={k?.verified_visits ?? 0} />
              <Card label="Interactions" value={k?.completed_interactions ?? 0} />
              <Card label="Overdue follow-ups" value={k?.overdue_followups ?? 0} detail="Needs action now" />
              <Card label="Average KM / verified visit" value={dashboard?.mileage.average_km_per_verified_visit ?? 0} detail={`${dashboard?.mileage.approved_km ?? 0} approved KM total`} />
            </div>
          </section>

          <section>
            <div className="mb-3 flex items-center gap-2"><WalletCards className="h-5 w-5" /><h2 className="font-semibold">My Pending Submissions</h2><Link className="ml-auto text-sm font-medium text-blue-700 hover:underline" to="/staff-invoices">View invoice history</Link></div>
            <div className="grid gap-3 md:grid-cols-3">
              <Card label="Reimbursement claims" value={pending?.reimbursements.count ?? 0} detail={money(pending?.reimbursements.amounts_by_currency)} />
              <Card label="Pending commissions" value={pending?.commissions.count ?? 0} detail={money(pending?.commissions.amounts_by_currency)} />
              <Card label="Invoices under review" value={pending?.invoices.count ?? 0} detail={money(pending?.invoices.amounts_by_currency)} />
            </div>
            <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pending-submissions-table">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
                    <th className="px-4 py-3 text-left">Submission</th><th className="px-4 py-3 text-left">Reference</th>
                    <th className="px-4 py-3 text-left">Submitted</th><th className="px-4 py-3 text-left">Amount</th>
                    <th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Waiting</th>
                  </tr></thead>
                  <tbody>{pending?.items.map(item => <tr key={`${item.type}-${item.reference}`} className="border-t border-slate-100">
                    <td className="px-4 py-3">{item.type}</td>
                    <td className="px-4 py-3">{item.detail_path ? <Link className="font-medium text-blue-700 hover:underline" to={item.detail_path}>{item.reference}</Link> : item.reference}</td>
                    <td className="px-4 py-3">{item.submitted_at ? new Date(item.submitted_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">{new Intl.NumberFormat(undefined, { style: "currency", currency: item.currency }).format(item.amount || 0)}</td>
                    <td className="px-4 py-3 capitalize">{item.status}</td><td className="px-4 py-3">{item.waiting_days} days</td>
                  </tr>)}</tbody>
                </table>
              </div>
              {!pending?.items.length && <div className="p-8 text-center text-sm text-slate-500">No submissions are waiting for approval.</div>}
            </div>
          </section>

          <section className="grid gap-5 lg:grid-cols-2">
            <div>
              <div className="mb-3 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /><h2 className="font-semibold">Needs Your Attention</h2></div>
              <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4" data-testid="needs-attention">
                {dashboard?.needs_attention.map(item => <div key={item.type} className="flex items-center justify-between rounded-md bg-amber-50 p-3 text-sm">
                  <span><strong>{item.count}</strong> {item.label}</span>
                  {item.detail_path && <Link to={item.detail_path} aria-label={`Open ${item.type}`}><ArrowRight className="h-4 w-4" /></Link>}
                </div>)}
                {!dashboard?.needs_attention.length && <p className="text-sm text-slate-500">Nothing needs correction right now.</p>}
              </div>
            </div>
            <div>
              <div className="mb-3 flex items-center gap-2"><ReceiptText className="h-5 w-5" /><h2 className="font-semibold">Money overview</h2></div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Card label="Approved reimbursements" value={money(dashboard?.mileage.approved_by_currency)} />
                <Card label="Pending reimbursements" value={money(dashboard?.mileage.pending_by_currency)} detail={`${dashboard?.mileage.rejected_count ?? 0} rejected`} />
                <Card label="Approved commissions" value={money(dashboard?.commissions.approved_by_currency)} />
                <Card label="Paid commissions" value={money(dashboard?.commissions.paid_by_currency)} detail={`${dashboard?.commissions.non_commissionable_conversions ?? 0} non-commissionable conversions`} />
              </div>
            </div>
          </section>

          <section>
            <div className="mb-3 flex items-center gap-2"><BriefcaseBusiness className="h-5 w-5" /><h2 className="font-semibold">Pipeline priorities</h2></div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {dashboard?.priority_pipeline.map(item => <Link key={item.id} to={item.detail_path} className="rounded-lg border border-slate-200 bg-white p-4 hover:border-blue-300">
                <div className="flex items-start justify-between gap-2"><h3 className="font-medium">{item.name}</h3><ArrowRight className="h-4 w-4 text-slate-400" /></div>
                <p className="mt-1 text-xs capitalize text-slate-500">{item.stage} · {item.aging_days} days since update</p>
                <p className="mt-3 font-semibold">{new Intl.NumberFormat(undefined, { style: "currency", currency: item.currency }).format(item.value || 0)}</p>
                <p className="text-xs text-slate-500">Expected close: {item.expected_close_date || "Not set"}</p>
              </Link>)}
              {!dashboard?.priority_pipeline.length && <div className="rounded-lg border border-dashed p-8 text-sm text-slate-500">No open opportunities in this period.</div>}
            </div>
          </section>
        </>}
      </main>
    </>
  );
}
